import { useState, useEffect, useCallback } from "react";
import { rtdb } from "../../services/firebase";
import { CHAT_CHANNEL_TYPING } from "../../types/db/chat";
import useDebounce from "../useDebounce";
import { ChatChannelService } from "../../types/services/chat";

/**
 * Hook which takes care of returning a listing of members who are typing in the channel
 * @param channelId The channel id of the channel
 * @param userId The current userId
 * @return an array of userID who are typing
 */
export const useTypingMembers = (channelId: string, userId?: string) => {
    const [isTyping, setIsTyping] = useState<boolean | string>(false);
    const [typingMembers, setTypingMembers] = useState<string[]>([]);
    const debounceIsTyping = useDebounce(isTyping, 5000);

    useEffect(() => {
        const typingRef = (new ChatChannelService(rtdb)).getEntityRef(channelId).child(CHAT_CHANNEL_TYPING);

        typingRef.on('value', (snapshot) => {
            const val = snapshot.val();
            setTypingMembers(Object.keys(val || {}));
        });

        return () => {
            //unsubscribe all listener
            typingRef.off();
            //reset the array of typing users
            setTypingMembers([]);
        }
    }, [channelId]);

    /** Either set if the user is currently typing or what is been typed (so automatically remove "typing" if entry is empty) */
    const setCurrentUserTyping = useCallback((isTyping: boolean | string) => {
        setIsTyping(currentValue => {
            if (currentValue !== isTyping && !!userId) {
                //only if there is a change in the state we would call the db to update
                //and only if we have a current user
                const service = new ChatChannelService(rtdb)
                if (!!isTyping) {
                    service.getEntityRef(channelId).child(CHAT_CHANNEL_TYPING)
                        .child(userId).set(true);
                } else {
                    service.getEntityRef(channelId).child(CHAT_CHANNEL_TYPING)
                        .child(userId).remove();
                }
            }

            return isTyping;
        });
    }, [channelId, userId]);

    useEffect(() => {
        if (!!debounceIsTyping) {
            setCurrentUserTyping(false);
        }
    }, [debounceIsTyping, setCurrentUserTyping]);

    return { typingMembers, setCurrentUserTyping }
}